import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, Icon, Tooltip, Typography } from '@ann/web-components';
import {
  EDIT_PERMISSION_LABELS,
  EDIT_PERMISSION_ORDER,
  PERMISSION_TYPES,
  SHARE_MODAL_COPY,
  VIEW_PERMISSIONS,
} from '../../../../constants';
import { normalizeViewPermissions } from '../../../../utils/permissionsHelpers';
import {
  CheckboxLabel,
  CheckboxRow,
  ChevronWrap,
  Panel,
  PanelContent,
  PanelScrollbar,
  PillButton,
  SectionHeader,
  SectionHeaderLabel,
  Wrapper,
} from './PermissionsDropdown.styled';

const PermissionsDropdown = ({
  permissionLevel = PERMISSION_TYPES.VIEW,
  permissionModes,
  editPermissions = [],
  viewPermissions = [VIEW_PERMISSIONS.VIEW_ONLY],
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [editExpanded, setEditExpanded] = useState(true);
  const [viewExpanded, setViewExpanded] = useState(true);
  const [createExpanded, setCreateExpanded] = useState(true);
  const rootRef = useRef(null);

  const selectedModes = useMemo(() => {
    if (Array.isArray(permissionModes) && permissionModes.length > 0) {
      const modes = Array.from(
        new Set(
          permissionModes.filter(
            (mode) =>
              mode === PERMISSION_TYPES.VIEW ||
              mode === PERMISSION_TYPES.EDIT ||
              mode === PERMISSION_TYPES.CREATE
          )
        )
      );
      return modes.includes(PERMISSION_TYPES.VIEW)
        ? modes
        : [PERMISSION_TYPES.VIEW, ...modes];
    }
    return permissionLevel === PERMISSION_TYPES.EDIT
      ? [PERMISSION_TYPES.VIEW, PERMISSION_TYPES.EDIT]
      : [PERMISSION_TYPES.VIEW];
  }, [permissionLevel, permissionModes]);

  const isEdit = selectedModes.includes(PERMISSION_TYPES.EDIT);
  const isView = selectedModes.includes(PERMISSION_TYPES.VIEW);
  const isCreate = selectedModes.includes(PERMISSION_TYPES.CREATE);
  const editSet = useMemo(() => new Set(editPermissions), [editPermissions]);
  const viewSet = useMemo(() => {
    return new Set(normalizeViewPermissions(viewPermissions));
  }, [viewPermissions]);

  const emitChange = useCallback(
    (nextModes, nextEdit, nextView = [VIEW_PERMISSIONS.VIEW_ONLY]) => {
      const rawModes =
        Array.isArray(nextModes) && nextModes.length > 0
          ? Array.from(new Set(nextModes))
          : [];
      const modes = rawModes.includes(PERMISSION_TYPES.VIEW)
        ? rawModes
        : [PERMISSION_TYPES.VIEW, ...rawModes];
      const nextLevel = modes.includes(PERMISSION_TYPES.EDIT)
        ? PERMISSION_TYPES.EDIT
        : PERMISSION_TYPES.VIEW;
      const resolvedView = normalizeViewPermissions(nextView);
      onChange?.({
        permissionLevel: nextLevel,
        permissionModes: modes,
        editPermissions: modes.includes(PERMISSION_TYPES.EDIT) ? nextEdit : [],
        viewPermissions: resolvedView,
      });
    },
    [onChange]
  );

  const handleToggleOpen = useCallback(() => {
    setOpen((v) => !v);
  }, []);

  const handleEditHeaderClick = useCallback(() => {
    setEditExpanded((v) => !v);
  }, []);

  const handleViewHeaderClick = useCallback(() => {
    setViewExpanded((v) => !v);
  }, []);

  const handleCreateHeaderClick = useCallback(() => {
    setCreateExpanded((v) => !v);
  }, []);

  const handleToggleCreate = useCallback(() => {
    const nextModes = isCreate
      ? selectedModes.filter((mode) => mode !== PERMISSION_TYPES.CREATE)
      : [...selectedModes, PERMISSION_TYPES.CREATE];
    emitChange(nextModes, editPermissions, Array.from(viewSet));
  }, [editPermissions, emitChange, isCreate, selectedModes, viewSet]);

  const handleToggleViewExportData = useCallback(() => {
    const next = new Set(viewSet);
    if (next.has(VIEW_PERMISSIONS.EXPORT_DATA)) {
      next.delete(VIEW_PERMISSIONS.EXPORT_DATA);
    } else {
      next.add(VIEW_PERMISSIONS.EXPORT_DATA);
    }
    emitChange(selectedModes, editPermissions, Array.from(next));
  }, [editPermissions, emitChange, selectedModes, viewSet]);

  const toggleSubPermission = useCallback(
    (key) => {
      const next = new Set(editSet);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      const nextModes = selectedModes.includes(PERMISSION_TYPES.EDIT)
        ? selectedModes
        : [...selectedModes, PERMISSION_TYPES.EDIT];
      emitChange(nextModes, Array.from(next), Array.from(viewSet));
    },
    [editSet, emitChange, selectedModes, viewSet]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocDown, { passive: true });
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  const summaryText = useMemo(() => {
    const baseParts = [];
    if (isEdit) {
      baseParts.push(SHARE_MODAL_COPY.permissions.summaryEdit);
    }
    if (isView) {
      baseParts.push(SHARE_MODAL_COPY.permissions.viewSection);
    }

    const extraCount =
      (Array.isArray(editPermissions) ? editPermissions.length : 0) +
      (isCreate ? 1 : 0) +
      (viewSet.has(VIEW_PERMISSIONS.EXPORT_DATA) ? 1 : 0);

    if (baseParts.length === 0) {
      return SHARE_MODAL_COPY.permissions.summaryView;
    }

    const visible = baseParts.slice(0, 2);
    const hiddenBase = baseParts.length - visible.length;
    const rest = hiddenBase + extraCount;

    return rest > 0 ? `${visible.join(', ')}, + ${rest}` : visible.join(', ');
  }, [editPermissions, isCreate, isEdit, isView, viewSet]);

  const tooltipContent = useMemo(() => {
    const selectedLines = [];

    if (isView) {
      selectedLines.push(SHARE_MODAL_COPY.permissions.viewItem);
      if (viewSet.has(VIEW_PERMISSIONS.EXPORT_DATA)) {
        selectedLines.push(SHARE_MODAL_COPY.permissions.viewExportItem);
      }
    }

    if (isEdit) {
      selectedLines.push(SHARE_MODAL_COPY.permissions.editSection);
      const activeEditLabels = EDIT_PERMISSION_ORDER
        .filter((key) => editPermissions.includes(key))
        .map((key) => EDIT_PERMISSION_LABELS[key])
        .filter(Boolean);
      selectedLines.push(...activeEditLabels);
    }

    if (isCreate) {
      selectedLines.push(SHARE_MODAL_COPY.permissions.createItem);
    }

    if (selectedLines.length === 0) {
      return SHARE_MODAL_COPY.permissions.tooltipEmptyEdit;
    }

    return (
      <div>
        {selectedLines.map((line) => (
          <div key={line}>{`• ${line}`}</div>
        ))}
      </div>
    );
  }, [editPermissions, isCreate, isEdit, isView, viewSet]);

  return (
    <Wrapper ref={rootRef}>
      <Tooltip content={tooltipContent} position="top" arrow={false}>
        <PillButton type="button" onClick={handleToggleOpen} aria-expanded={open} aria-haspopup="listbox">
          <Typography category="body" size="medium" weight="medium" color="label">
            {summaryText}
          </Typography>
          <Icon name="chevronDown" size="medium" color="neutral" />
        </PillButton>
      </Tooltip>

      {open && (
        <Panel role="listbox">
          <PanelScrollbar>
            <PanelContent>
              <div>
                <SectionHeader type="button" onClick={handleEditHeaderClick}>
                  <SectionHeaderLabel>{SHARE_MODAL_COPY.permissions.editSection}</SectionHeaderLabel>
                  <ChevronWrap aria-hidden>
                    <Icon name={editExpanded ? 'chevronUp' : 'chevronDown'} size="medium" color="neutral" />
                  </ChevronWrap>
                </SectionHeader>
                {editExpanded &&
                  EDIT_PERMISSION_ORDER.map((key) => (
                    <CheckboxRow key={key}>
                      <Checkbox
                        size="small"
                        checked={isEdit && editSet.has(key)}
                        onChange={() => {
                          if (!isEdit) {
                            emitChange(
                              [...selectedModes, PERMISSION_TYPES.EDIT],
                              [key],
                              Array.from(viewSet)
                            );
                            return;
                          }
                          toggleSubPermission(key);
                        }}
                      />
                      <CheckboxLabel as="span">
                        <Typography category="body" size="small" weight="regular" color="body">
                          {EDIT_PERMISSION_LABELS[key]}
                        </Typography>
                      </CheckboxLabel>
                    </CheckboxRow>
                  ))}
              </div>

              <SectionHeader type="button" onClick={handleViewHeaderClick}>
                <SectionHeaderLabel>{SHARE_MODAL_COPY.permissions.viewSection}</SectionHeaderLabel>
                <ChevronWrap aria-hidden>
                  <Icon name={viewExpanded ? 'chevronUp' : 'chevronDown'} size="medium" color="neutral" />
                </ChevronWrap>
              </SectionHeader>
              {viewExpanded && (
                <>
                  <CheckboxRow>
                    <Checkbox size="small" checked={isView} disabled />
                    <CheckboxLabel as="span">
                      <Typography category="body" size="small" weight="regular" color="body">
                        {SHARE_MODAL_COPY.permissions.viewItem}
                      </Typography>
                    </CheckboxLabel>
                  </CheckboxRow>
                  <CheckboxRow>
                    <Checkbox
                      size="small"
                      checked={viewSet.has(VIEW_PERMISSIONS.EXPORT_DATA)}
                      onChange={handleToggleViewExportData}
                    />
                    <CheckboxLabel as="span">
                      <Typography category="body" size="small" weight="regular" color="body">
                        {SHARE_MODAL_COPY.permissions.viewExportItem}
                      </Typography>
                    </CheckboxLabel>
                  </CheckboxRow>
                </>
              )}

              <SectionHeader type="button" onClick={handleCreateHeaderClick}>
                <SectionHeaderLabel>{SHARE_MODAL_COPY.permissions.createSection}</SectionHeaderLabel>
                <ChevronWrap aria-hidden>
                  <Icon name={createExpanded ? 'chevronUp' : 'chevronDown'} size="medium" color="neutral" />
                </ChevronWrap>
              </SectionHeader>
              {createExpanded && (
                <CheckboxRow>
                  <Checkbox
                    size="small"
                    checked={isCreate}
                    onChange={handleToggleCreate}
                  />
                  <CheckboxLabel as="span">
                    <Typography category="body" size="small" weight="regular" color="body">
                      {SHARE_MODAL_COPY.permissions.createItem}
                    </Typography>
                  </CheckboxLabel>
                </CheckboxRow>
              )}
            </PanelContent>
          </PanelScrollbar>
        </Panel>
      )}
    </Wrapper>
  );
};

export default memo(PermissionsDropdown);
